import React, { useState, useEffect } from "react";
import Dialog from "./Dialog/Dialog";

const EnrollmentForm = ({ onStudentDataChange }) => {
  const [studentData, setStudentData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    photo: null,
    dateOfBirth: "",
    gender: "",
    className: "",
    studentId: "",
    fathersName: "",
    mothersName: "",
    address: "",
    phoneNumber: "",
    alternativePhoneNumber: "",
    email: "",
  });

  const [classes, setClasses] = useState([]);
  const [errors, setErrors] = useState({});
  const [dialog, setDialog] = useState({
    isOpen: false,
    type: "",
    title: "",
    message: "",
  });

  const generateNewStudentId = () => {
    const currentYear = new Date().getFullYear();
    const randomNumber = Math.floor(10000 + Math.random() * 90000);
    return `${currentYear}${randomNumber}`;
  };

  const resetForm = () => {
    setStudentData({
      firstName: "",
      middleName: "",
      lastName: "",
      photo: null,
      dateOfBirth: "",
      gender: "",
      className: "",
      studentId: generateNewStudentId(),
      fathersName: "",
      mothersName: "",
      address: "",
      phoneNumber: "",
      alternativePhoneNumber: "",
      email: "",
    });
    setErrors({});
  };

  useEffect(() => {
    const newStudentId = generateNewStudentId();
    setStudentData((prevState) => ({
      ...prevState,
      studentId: newStudentId,
    }));
    onStudentDataChange((prevState) => ({
      ...prevState,
      studentId: newStudentId,
    }));

    // 🔁 Mock class data
    const mockClasses = [
      { id: 1, class_name: "Nursery" },
      { id: 2, class_name: "LKG" },
      { id: 3, class_name: "UKG" },
      { id: 4, class_name: "Grade 1" },
      { id: 5, class_name: "Grade 2" },
    ];
    setClasses(mockClasses);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedStudentData = { ...studentData, [name]: value };
    setStudentData(updatedStudentData);
    onStudentDataChange(updatedStudentData);
    validateFields(name, value);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const updatedStudentData = { ...studentData, photo: file };
    setStudentData(updatedStudentData);
    onStudentDataChange(updatedStudentData);
  };

  const validateFields = (name, value) => {
    const newErrors = { ...errors };

    if (["phoneNumber", "alternativePhoneNumber"].includes(name)) {
      const phonePattern = /^[0-9]{10}$/;
      newErrors[name] = phonePattern.test(value)
        ? ""
        : "Phone number must be 10 digits";
    }

    if (name === "email") {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      newErrors.email = emailPattern.test(value) ? "" : "Invalid email address";
    }

    setErrors(newErrors);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (Object.values(errors).some((error) => error)) {
      setDialog({
        isOpen: true,
        type: "error",
        title: "Validation Error",
        message: "Please fix the errors before submitting.",
        onConfirm: () => setDialog({ isOpen: false }),
      });
      return;
    }

    // ✅ Simulated submission
    console.log("Mock student data submitted:", studentData);
    setDialog({
      isOpen: true,
      type: "success",
      title: "Success",
      message: "Student data saved locally (mocked)!",
      onConfirm: () => {
        setDialog({ isOpen: false });
        resetForm();
      },
    });
  };

  return (
    <>
      <div className="enrollment-form-section box">
        <h2 className="main-heading">Enrollment Form</h2>
        <form
          className="enrollment-form"
          onSubmit={handleSubmit}
          encType="multipart/form-data"
        >
          <h5 className="sub-heading">Student Details</h5>
          <div className="form-row">
            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                className="form-control"
                name="firstName"
                value={studentData.firstName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Middle Name</label>
              <input
                type="text"
                name="middleName"
                className="form-control"
                value={studentData.middleName}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                className="form-control"
                name="lastName"
                value={studentData.lastName}
                onChange={handleChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Upload Photo</label>
              <input
                type="file"
                className="form-control"
                name="photo"
                onChange={handleFileChange}
                key={studentData.studentId}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Date of Birth</label>
              <input
                type="date"
                className="form-control"
                name="dateOfBirth"
                value={studentData.dateOfBirth}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Gender</label>
              <select
                className="form-control"
                name="gender"
                value={studentData.gender}
                onChange={handleChange}
              >
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-group">
              <label>Enrolled To Class</label>
              <select
                className="form-control"
                name="className"
                value={studentData.className}
                onChange={handleChange}
                required
              >
                <option value="">Select a class</option>
                {classes.map((classItem) => (
                  <option key={classItem.id} value={classItem.class_name}>
                    {classItem.class_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <input type="hidden" name="studentId" value={studentData.studentId} />
          <h5 className="sub-heading">Guardian Details</h5>
          <div className="form-row">
            <div className="form-group">
              <label>Father's Name</label>
              <input
                type="text"
                className="form-control"
                name="fathersName"
                value={studentData.fathersName}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Mother's Name</label>
              <input
                type="text"
                className="form-control"
                name="mothersName"
                value={studentData.mothersName}
                onChange={handleChange}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Address</label>
              <input
                type="text"
                className="form-control"
                name="address"
                value={studentData.address}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="number"
                className="form-control"
                name="phoneNumber"
                value={studentData.phoneNumber}
                onChange={handleChange}
              />
              {errors.phoneNumber && (
                <p className="error-text">{errors.phoneNumber}</p>
              )}
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Alternative Phone Number</label>
              <input
                type="number"
                className="form-control"
                name="alternativePhoneNumber"
                value={studentData.alternativePhoneNumber}
                onChange={handleChange}
              />
              {errors.alternativePhoneNumber && (
                <p className="error-text">{errors.alternativePhoneNumber}</p>
              )}
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                className="form-control"
                name="email"
                value={studentData.email}
                onChange={handleChange}
              />
              {errors.email && <p className="error-text">{errors.email}</p>}
            </div>
          </div>
          <button type="submit" className="badge bg-success">
            Submit
          </button>
        </form>
      </div>
      <Dialog
        isOpen={dialog.isOpen}
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        onConfirm={dialog.onConfirm}
        onCancel={dialog.onCancel}
      />
    </>
  );
};

export default EnrollmentForm;
